// Exhibitor directory data (v0.7). BUNDLED, not fetched: the whole directory ships
// inside the build, so it can't fail on the show floor and needs no signal, no
// snapshot, and no sign-in. Source files live in data/exhibitors/; a build step
// (scripts/build-exhibitors.mjs) groups + trims them into one generated JSON.
//
// The import is DYNAMIC so Vite emits the data as its own chunk: the app boots
// (Capture) without parsing ~190 KB of exhibitors, and the chunk is still precached by
// workbox (globPatterns covers **/*.js), so first offline use works too.

import { normalize } from '../vendors/match';
import type { Show } from '../config';

/** One company at one show, with every booth it holds there. */
export interface Exhibitor {
  name: string;
  /** Source `booth_id`, where the organizer publishes one. See `exhibitorKey`. */
  id: string | null;
  /** Booth numbers, in source order. Empty for shows with no assignments yet. */
  booths: string[];
}

export interface ShowDirectory {
  /** URL slug for #/show/{key}. */
  key: string;
  /** The app's controlled Show value — matches what a BoothRecord stores. */
  show: Show;
  /** Full show name, e.g. "ASD Market Week August 2026". */
  label: string;
  dates: string | null;
  venue: string | null;
  pulled_at: string | null;
  /** Upstream list is known-incomplete (booths unassigned, or a capped pull). */
  partial: boolean;
  partial_note: string | null;
  count: number;
  /** False for shows whose booths aren't assigned yet (WWIN/Evolve). */
  has_booths: boolean;
  /** Set when we have no list at all (OffPrice) — the reason, for the UI. */
  unavailable: string | null;
  /**
   * Slug of the show whose directory actually carries this show's exhibitors, when
   * this one has no list of its own. OffPrice is co-located on the MAGIC/Informa
   * floor and its vendors aren't separable from that pull, so the Info tab renders
   * OffPrice as a tap-through to `magic` rather than as missing data. Null for every
   * show that stands on its own.
   */
  see_show: string | null;
  exhibitors: Exhibitor[];
}

// Shape as emitted by scripts/build-exhibitors.mjs (compact keys: n/i/b).
interface RawShow {
  key: string;
  show: string;
  label: string;
  dates?: string | null;
  venue?: string | null;
  pulled_at?: string | null;
  partial?: boolean;
  partial_note?: string | null;
  count: number;
  has_booths: boolean;
  unavailable?: string;
  see_show?: string | null;
  exhibitors: { n: string; i: string | null; b: string[] }[];
}

let cache: Promise<ShowDirectory[]> | null = null;

/** All six shows, in manifest order. Loads (and caches) the bundled chunk. */
export function loadShows(): Promise<ShowDirectory[]> {
  if (!cache) {
    cache = import('../data/exhibitors.generated.json')
      .then((mod) => {
        const raw = (mod.default ?? mod) as unknown as { shows: RawShow[] };
        return raw.shows.map(
          (s): ShowDirectory => ({
            key: s.key,
            show: s.show,
            label: s.label,
            dates: s.dates ?? null,
            venue: s.venue ?? null,
            pulled_at: s.pulled_at ?? null,
            partial: !!s.partial,
            partial_note: s.partial_note ?? null,
            count: s.count,
            has_booths: s.has_booths,
            unavailable: s.unavailable ?? null,
            see_show: s.see_show ?? null,
            exhibitors: s.exhibitors.map((e) => ({ name: e.n, id: e.i, booths: e.b })),
          }),
        );
      })
      .catch((err) => {
        // Bundled data failing to load means the chunk is missing from the cache —
        // don't poison the module cache, so a later attempt can retry.
        cache = null;
        throw err;
      });
  }
  return cache;
}

/** One show by its URL slug, or undefined. */
export async function getShowDirectory(key: string): Promise<ShowDirectory | undefined> {
  const shows = await loadShows();
  return shows.find((s) => s.key === key.toLowerCase());
}

/**
 * Stable identity for an exhibitor, used to key hit-list stars so they survive the
 * pre-trip data re-pull.
 *
 * Per the brief: the organizer's `booth_id` where we have one, else name+show. The
 * wrinkle the data revealed is that ASD's `booth_id` identifies a BOOTH, not a
 * company (1,440 ids for 1,119 companies) — so a company that adds a booth before
 * the show can come back under a different first id. Every hit-list target therefore
 * ALSO carries `nameKey` (below) and matching accepts either, so a star survives both
 * a booth add (name holds) and a rename (id holds). See decisions.md v0.7.0.
 */
export function exhibitorKey(showKey: string, ex: Exhibitor): string {
  return ex.id ? `id:${showKey}:${ex.id}` : nameKey(showKey, ex);
}

/** Name-based identity — the secondary match, and the primary where there's no id. */
export function nameKey(showKey: string, ex: Exhibitor): string {
  return `name:${showKey}:${normalize(ex.name)}`;
}

/**
 * "2026-08-25/2026-08-27" → "Aug 25–27, 2026". Falls back to the raw string, so a
 * date format we didn't anticipate still shows something true. Lives here (not in
 * a screen) because both the Info tab's Shows list and the directory header use it.
 */
export function fmtShowDates(range: string | null | undefined): string {
  if (!range) return '';
  const [from, to] = range.split('/');
  const a = new Date(from);
  const b = new Date(to ?? from);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return range;
  const month = a.toLocaleDateString(undefined, { month: 'short' });
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  return sameMonth
    ? `${month} ${a.getDate()}–${b.getDate()}, ${a.getFullYear()}`
    : `${a.toLocaleDateString()} – ${b.toLocaleDateString()}`;
}
